import { capitalize, lowerCase } from "lodash";

export type ConfiguredField = {
  value: string;
  placeholder?: string;
  regexPattern?: string;
  maxLength?: number;
  [key: string]: unknown;
};

const SAMPLE_VIN = "1HGCM82633A004352";

// Formats that are the same at every carrier; anything else (e.g. policy numbers) varies too much to show one.
const UNIVERSAL_EXAMPLES: Record<string, string> = {
  postalCode: "94107",
  zipcode: "94107",
  firstName: "Jane",
  lastName: "Smith",
  fullName: "Jane Smith",
  email: "jane@example.com",
  phone: "(555) 123-4567",
};

// Labels are app copy keyed by field key; a config's text is only ever the hint inside the box.
const FIELD_LABELS: Record<string, string> = {
  policyNumber: "Policy number",
  vin: "VIN",
  postalCode: "ZIP code",
  zipcode: "ZIP code",
  houseNumber: "House number",
  addressLine1: "Street address",
  firstName: "First name",
  lastName: "Last name",
  fullName: "Full name",
  email: "Email",
  phone: "Phone number",
  policyType: "Policy type",
};

// A form passes `labels` where its fields use a key for something else.
export const getFieldLabel = (
  field: ConfiguredField,
  labels: Record<string, string> = {}
) => labels[field.value] ?? FIELD_LABELS[field.value] ?? capitalize(lowerCase(field.value));

const getUniversalExample = (field: ConfiguredField) => {
  if (field.value === "vin") {
    // VINs are a fixed 17-character standard; show only as many characters as the field allows.
    return `e.g. ${SAMPLE_VIN.slice(-Math.min(field.maxLength || SAMPLE_VIN.length, SAMPLE_VIN.length))}`;
  }
  const example = UNIVERSAL_EXAMPLES[field.value];
  return example && `e.g. ${example}`;
};

// Hint inside a configured field: the config's text verbatim, else a universal example, else none.
export const getFieldHint = (field: ConfiguredField) =>
  field.placeholder || getUniversalExample(field);

// Field name as used inside an error sentence: lowercase, but keep acronyms like VIN.
const toFieldName = (label: string) =>
  label
    .split(" ")
    .map((word) => (/[A-Z]{2,}/.test(word) ? word : word.toLowerCase()))
    .join(" ");

export const getMissingFieldError = (
  field: ConfiguredField,
  labels: Record<string, string> = {}
) => `Enter your ${toFieldName(getFieldLabel(field, labels))}`;

export const getInvalidFieldError = (field: ConfiguredField) =>
  `Enter a valid ${toFieldName(getFieldLabel(field))}`;
